import React, { useMemo } from 'react';
import _ from 'lodash';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Handshake as HandshakeIcon,
  BusinessCenter as BusinessCenterIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import { InfoCard, formatNumber, ProfitVisualization } from './CommonComponents';

/**
 * 개선된 매출이익 정보 표시 컴포넌트 (V2)
 * 
 * 개선사항:
 * - 시각적 차트로 비율 표현
 * - 카드형 레이아웃
 * - 금액/비율 강조
 * - 내부거래 테이블 개선
 */
const SalesProfitInfoV2 = ({ orderInfo, project }) => {
  // 총 매출액
  const totalSales = project?.totalSales || 0;

  // 퍼센트 계산 함수
  const getPercent = (value) => {
    if (!totalSales || totalSales === 0) return 0;
    return ((Number(value || 0) / totalSales) * 100).toFixed(1);
  };

  // 내부거래비용 배열 처리
  const roInternalCosts = useMemo(() => {
    if (orderInfo?.roInternalCosts && orderInfo.roInternalCosts.length > 0) {
      return orderInfo.roInternalCosts;
    }
    if (orderInfo?.roPartnerUsrUuid) {
      return [{
        roPartnerUsrUuid: orderInfo.roPartnerUsrUuid,
        roPartnerUsrNm: orderInfo.roPartnerUsrNm,
        roInternalAmt: orderInfo.roInternalAmt,
        roInternalAmtPercent: getPercent(orderInfo.roInternalAmt),
      }];
    }
    return [];
  }, [orderInfo]);

  // 총 내부거래비용
  const totalInternalAmt = useMemo(() => {
    return _.sumBy(roInternalCosts, 'roInternalAmt') || 0;
  }, [roInternalCosts]);

  // 매출이익
  const roProfitAmt = Number(orderInfo?.roProfitAmt || 0);
  const roProfitPercent = orderInfo?.roProfitAmtPercent || getPercent(roProfitAmt);

  // 영업비용
  const roCostAmt = Number(orderInfo?.roCostAmt || 0);
  const roCostPercent = orderInfo?.roCostAmtPercent || getPercent(roCostAmt);

  // 최종 매출이익
  const roAdjProfitAmt = Number(orderInfo?.roAdjProfitAmt) || (roProfitAmt - roCostAmt - totalInternalAmt);
  const roAdjProfitPercent = orderInfo?.roAdjProfitAmtPercent || getPercent(roAdjProfitAmt);

  // 수익성 상태
  const profitStatus = roAdjProfitAmt >= 0 ? 'positive' : 'negative';

  return (
    <Box>
      {/* 매출이익 시각화 */}
      <ProfitVisualization
        totalSales={totalSales}
        profit={roProfitAmt}
        cost={roCostAmt}
        internalCost={totalInternalAmt}
      />

      {/* 상세 정보 카드들 */}
      <Stack spacing={2}>
        {/* 매출이익 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'success.main',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 44, 
                height: 44, 
                borderRadius: '50%', 
                bgcolor: 'success.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <TrendingUpIcon color="success" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  매출이익
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatNumber(roProfitAmt)}원
                </Typography>
              </Box>
            </Stack>
            <Chip 
              label={`${roProfitPercent}%`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '1rem', height: 36 }}
            />
          </Stack>
        </Paper>

        {/* 내부거래비용 */}
        {roInternalCosts.length > 0 && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2.5,
              borderRadius: 2,
              borderLeft: '4px solid',
              borderLeftColor: 'info.main',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ 
                width: 44, 
                height: 44, 
                borderRadius: '50%', 
                bgcolor: 'info.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <HandshakeIcon color="info" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  내부거래비용
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  총 {formatNumber(totalInternalAmt)}원
                </Typography>
              </Box>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>협업본부 영업담당</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>거래비용</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>매출액 대비</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roInternalCosts.map((row, index) => (
                    <TableRow key={row?.roPartnerUsrUuid || index}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                            {row?.roPartnerUsrNm?.[0]}
                          </Avatar>
                          <Typography variant="body2">
                            {row?.roPartnerUsrNm || '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatNumber(row?.roInternalAmt)}원
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${row?.roInternalAmtPercent || getPercent(row?.roInternalAmt)}%`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* 내부거래비용 없음 */}
        {roInternalCosts.length === 0 && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2.5,
              borderRadius: 2,
              borderLeft: '4px solid',
              borderLeftColor: 'grey.300',
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 44, 
                height: 44, 
                borderRadius: '50%', 
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <HandshakeIcon color="disabled" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                등록된 내부 거래비용이 없습니다.
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* 영업비용 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2.5,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: 'warning.main',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 44, 
                height: 44, 
                borderRadius: '50%', 
                bgcolor: 'warning.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <BusinessCenterIcon color="warning" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  영업비용
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatNumber(roCostAmt)}원
                </Typography>
              </Box>
            </Stack>
            <Chip 
              label={`${roCostPercent}%`}
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '1rem', height: 36 }}
            />
          </Stack>
        </Paper>

        {/* 최종 매출이익 */}
        <Paper 
          elevation={2}
          sx={{ 
            p: 3,
            borderRadius: 2,
            background: profitStatus === 'positive' 
              ? 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)'
              : 'linear-gradient(135deg, #ffebee 0%, #ffffff 100%)',
            border: '2px solid',
            borderColor: profitStatus === 'positive' ? 'success.main' : 'error.main',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                width: 52, 
                height: 52, 
                borderRadius: '50%', 
                bgcolor: profitStatus === 'positive' ? 'success.main' : 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <EmojiEventsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  최종 매출이익
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={700}
                  color={profitStatus === 'positive' ? 'success.main' : 'error.main'}
                >
                  {formatNumber(roAdjProfitAmt)}원
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Chip 
                icon={profitStatus === 'positive' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${roAdjProfitPercent}%`}
                color={profitStatus === 'positive' ? 'success' : 'error'}
                sx={{ fontWeight: 700, fontSize: '1.1rem', height: 40, px: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                매출액 대비 이익률
              </Typography>
            </Box>
          </Stack>

          {/* 계산식 표시 */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            매출이익 ({formatNumber(roProfitAmt)}) - 내부거래 ({formatNumber(totalInternalAmt)}) - 영업비용 ({formatNumber(roCostAmt)}) = 최종이익 ({formatNumber(roAdjProfitAmt)})
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
};

export default SalesProfitInfoV2;
